/**
 * Phase 20 — auto-send per campaign group. A master config holds its group's
 * switch, daily cap and stop-loss; every container of the group runs under it.
 * A container may add its own layer on top ("Own settings"): its switch can
 * only take it out, its cap and stop-loss add to the group's, never replace
 * them. Kill and the technical halt stay country-wide (the plan).
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenSendToday } from "../api.types";
import type { DropGenStopLossCheck } from "../verdict.types";

/** One auto-send layer — a group's (master config) or a container's own. */
export interface DropGenAutoSendConfig {
  autoSend: boolean;
  dispatchMode?: "DUE_TIME" | "SCHEDULE_AHEAD";
  /** Phase 29 — shadow mode: the engine decides exactly as when on (layers,
   *  window, caps, stop-loss) but sends nothing — it writes "would have sent"
   *  and the draft waits for a person. Read only while autoSend is true. */
  shadow: boolean;
  /** null = no cap. Every SMS sent today from the layer's drafts, engine or person. */
  dailySmsCap: number | null;
  /** GateNode over the layer's settled sends of the day. */
  stopLossJson: Record<string, unknown> | null;
  /** 23/10/2026 — the stop-loss's own switch: off keeps the rule, nothing is
   *  judged. Absent (older payloads) = on when there is a rule — read it with stopLossOn. */
  stopLossEnabled?: boolean;
  stopLossMinSampleSent: number;
  stopLossDelayMin: number;
  /** A stop-loss halt — for its run day only. A person's Resume clears
   *  haltedAt and keeps haltRunDate: that day the rule does not halt it again. */
  haltedAt: string | null;
  haltReason: string | null;
  haltRunDate: string | null;
}

/** One campaign group's auto-send today — the country page's table row. */
export interface DropGenGroupSendStatus {
  masterId: string;
  name: string;
  color: string;
  containerCount: number;
  config: DropGenAutoSendConfig;
  /** A stop-loss halt in force today. */
  haltedToday: boolean;
  /** Today's sends of the group's containers; null before the day exists. */
  sendToday: DropGenSendToday | null;
}

/** A stop-loss rule judged on today's numbers as they were last collected —
 *  the editor's "Right now" (zero writes, no tracker call). */
export interface DropGenStopLossPreview {
  /** null = no day yet (nothing laid out today, or no time zone). */
  check: DropGenStopLossCheck | null;
  /** Today's sent drops of the layer, and how many have their stats collected. */
  sentDrops: number;
  collectedDrops: number;
  /** When the newest of those stats was collected (ISO). */
  statsAt: string | null;
}

/** The three states of one layer's auto-send (user, 20/10/2026: Off ·
 *  Simulated · Live). Stored values: "shadow" = Simulated — on the real
 *  containers, deciding exactly as Live, sending nothing; "on" = Live. */
export type DropGenAutoSendMode = "off" | "shadow" | "on";

export const autoSendModeOf = (c: Pick<DropGenAutoSendConfig, "autoSend" | "shadow">): DropGenAutoSendMode =>
  !c.autoSend ? "off" : c.shadow ? "shadow" : "on";

/** Where a Live send goes on this server: real SMS (production, or a
 *  development server started with DROP_GENERATOR_AUTOSEND_REAL=true), or
 *  made-up stats (a development server with the simulation on — never
 *  production). Neither = this server never sends. */
export interface DropGenServerSendModes {
  real: boolean;
  simulated: boolean;
}

/** 20/10/2026 — putting a group in Live needs this; in Simulated the other (admins). */
export const AUTOSEND_REAL_PERMISSION = "drop_generator.autosend_real";
export const AUTOSEND_SIMULATE_PERMISSION = "drop_generator.autosend_simulate";

export const DROP_GEN_AUTOSEND_DEFAULTS: DropGenAutoSendConfig = {
  autoSend: false,
  dispatchMode: "DUE_TIME",
  shadow: false,
  dailySmsCap: null,
  stopLossJson: null,
  stopLossEnabled: false,
  /** 20/10/2026 — the guards are optional: 0 = off (never judged on zero SMS, see the engine's sampleFloor). */
  stopLossMinSampleSent: 0,
  stopLossDelayMin: 0,
  haltedAt: null,
  haltReason: null,
  haltRunDate: null,
};

/** Is the layer halted for this run day (YYYY-MM-DD)? */
export const haltedOn = (c: Pick<DropGenAutoSendConfig, "haltedAt" | "haltRunDate">, runDate: string) =>
  c.haltedAt !== null && c.haltRunDate !== null && c.haltRunDate.slice(0, 10) === runDate;

/** Did a person resume the layer's stop-loss halt this run day? Its rule
 *  then does not halt it again until tomorrow. */
export const resumedOn = (c: Pick<DropGenAutoSendConfig, "haltedAt" | "haltRunDate">, runDate: string) =>
  c.haltedAt === null && c.haltRunDate !== null && c.haltRunDate.slice(0, 10) === runDate;

/** The stop-loss judges this layer: switched on (23/10/2026) and a rule set. */
export const stopLossOn = (c: Pick<DropGenAutoSendConfig, "stopLossEnabled" | "stopLossJson">): boolean =>
  (c.stopLossEnabled ?? c.stopLossJson !== null) && c.stopLossJson !== null;
